import React, { createContext, ReactNode, useContext } from "react";
import createCache, { EmotionCache } from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { Theme, ThemeOptions } from "@mui/material/styles";
import { prefixer } from "stylis";
import { stylisRtl } from "./stylisRtl";

/**
 * Справа налево для MUI — только в арабском интерфейсе (shared/config/arabicUi).
 *
 * Свой кэш emotion (ключ «muirtl») с зеркалящим плагином перед префиксами
 * (как в руководстве MUI по RTL) и тема с direction: "rtl" — по ней MUI
 * поворачивает то, что считает в JS (Slider, Tabs, LinearProgress, Drawer…).
 * Всем остальным App эти обёртки не ставит вовсе: дерево и стили — сегодняшние.
 */
/** Кэш emotion, зеркалящий стили; `prepend` — как у кэша, который он заменяет. */
export const createRtlEmotionCache = (key: string, prepend?: boolean): EmotionCache =>
    createCache({ key, prepend, stylisPlugins: [stylisRtl, prefixer] });

let cache: EmotionCache | null = null;

export const rtlEmotionCache = (): EmotionCache => {
    if (!cache) cache = createRtlEmotionCache("muirtl");
    return cache;
};

let theme: Theme | null = null;

/** Тема по умолчанию справа налево (глобальной темы у сайта нет — MUI берёт свою). */
export const rtlTheme = (): Theme => {
    if (!theme) theme = createTheme({ direction: "rtl" });
    return theme;
};

/** Своя тема страницы (DietPage) справа налево. */
export const withRtl = (options: ThemeOptions, rtl: boolean): ThemeOptions => (rtl ? { ...options, direction: "rtl" } : options);
/** Идёт ли страница справа налево — ставит App, читают страницы со своими темами MUI. */
export const RtlContext = createContext(false);

export const useIsRtl = (): boolean => useContext(RtlContext);

export const RtlProviders: React.FC<{ children: ReactNode }> = ({ children }) => (
    <CacheProvider value={rtlEmotionCache()}>
        <ThemeProvider theme={rtlTheme()}>{children}</ThemeProvider>
    </CacheProvider>
);
